import { useState } from "react";
import clsx from "clsx";
import { db, eq } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { setQuery, useRoute } from "../../lib/router";
import { AREA_LABEL, useRefData, type Area } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { NextStepSheet } from "../../components/NextStep";
import { Empty, ErrorBox, Loading, Seg, fmtDate, fmtShort, money, useToast } from "../../components/ui";
import { OppSheet } from "../../components/OppSheet";

type Opp = { id: string; person_id: string; full_name: string; source_area: Area; board: string; stage: string; next_action: string | null; next_action_on: string | null; est_value: number | null; outcome: string; is_stale: boolean; is_overdue: boolean; missing_next_action: boolean; days_idle: number; notes: string | null };

export default function Pipeline() {
  const ref = useRefData();
  const { can } = useSession();
  const toast = useToast();
  const { query } = useRoute();
  const board = query.get("board") || "Solution";
  const outcome = query.get("outcome") || "Open";
  const [over, setOver] = useState<string | null>(null);
  const [editing, setEditing] = useState<Opp | "new" | null>(null);
  const editable = can("pipelines", "edit");

  const opps = useQuery(`pipeline:${board}:${outcome}`, () =>
    db.select<Opp>("opportunity_status", { select: "*", board: eq(board), outcome: eq(outcome), order: outcome === "Open" ? "next_action_on.asc.nullsfirst" : "closed_on.desc.nullslast" }));
  const stages = ref.data?.stagesFor(board) ?? [];
  // who each deal covers when it isn't the owner (a child on the parent's policy)
  const livesIds = [...new Set((opps.data ?? []).flatMap((o: any) => (o.solutions ?? []).map((s: any) => s.insured_person_id).filter(Boolean)))] as string[];
  const lives = useQuery(livesIds.length ? `pipeline:lives:${livesIds.join(",")}` : null, () => db.select<{ id: string; first_name: string | null; full_name: string }>("people", { id: `in.(${livesIds.join(",")})`, select: "id,first_name,full_name" }));
  const forWhom = (o: any) => [...new Set((o.solutions ?? []).map((s: any) => s.insured_person_id).filter((x: string) => x && x !== o.person_id))]
    .map((id) => lives.data?.find((p) => p.id === id)?.first_name ?? lives.data?.find((p) => p.id === id)?.full_name).filter(Boolean).join(", ");
  const mon = useQuery(outcome === "Open" ? `pipeline:money:${board}` : null, () => db.rpc<any>("pipeline_money", { p_board: board }));
  const stageMoney = (st: string) => (mon.data?.stages ?? []).find((x: any) => x.stage === st);

  async function move(id: string, stage: string) {
    const o = opps.data?.find((x) => x.id === id);
    if (!o || o.stage === stage) return;
    // a booked stage needs the meeting time, so open the card instead of moving silently
    if (/booked/i.test(stage)) return setEditing({ ...o, stage, _from: o.stage } as any);
    // a won stage closes the deal: no next step needed
    if (ref.data?.isWon(board, stage)) {
      await db.update("opportunities", { id: eq(id) }, { stage });
      invalidate("pipeline"); invalidate("cc");
      return toast(`${o.full_name} moved to ${stage}`);
    }
    setMoving({ id, stage });   // ask "what's next, and when?"
  }
  const [moving, setMoving] = useState<{ id: string; stage: string } | null>(null);

  return (
    <>
      <PageHead help="pipelines" title="Pipelines" sub="Real opportunities only. Every open card needs a next action and a date."
        actions={editable && <button className="btn primary" onClick={() => setEditing("new")}>Add opportunity</button>} />
      <div className="toolbar">
        <Seg value={board} onChange={(v) => setQuery({ board: v })} options={(ref.data?.boards ?? ["Solution", "Business"]).map((b) => ({ value: b, label: b }))} />
        <Seg value={outcome} onChange={(v) => setQuery({ outcome: v })} options={["Open", "Won", "Lost", "Not now"].map((o) => ({ value: o, label: o }))} />
      </div>
      <ErrorBox error={opps.error} />
      {mon.data && (
        <div className="tiles" style={{ marginBottom: 14 }}>
          <div className="tile t-navy"><span className="tv">{money(mon.data.value)}</span><span className="tl">Open pipeline</span><span className="ts">{mon.data.open} deals{mon.data.no_value ? ` · ${mon.data.no_value} without a value` : ""}</span></div>
          <div className="tile t-gold"><span className="tv">{money(mon.data.weighted)}</span><span className="tl">Weighted forecast</span><span className="ts">Value × chance at each stage</span></div>
          <div className="tile t-green"><span className="tv">{money(mon.data.won_this_month)}</span><span className="tl">Won this month</span><span className="ts">{mon.data.won_count != null ? `${mon.data.won_count} deal${mon.data.won_count === 1 ? "" : "s"} · ${Number(mon.data.won_points ?? 0).toLocaleString()} points · premium${mon.data.won_no_premium ? ` (${mon.data.won_no_premium} without one: Est. value used)` : ""}` : "Premium of deals won this month"}</span></div>
          <div className={clsx("tile", mon.data.stale ? "t-amber alert" : "t-green")}><span className="tv">{mon.data.stale}</span><span className="tl">Stale deals</span><span className="ts">No activity past your limit</span></div>
        </div>
      )}
      {opps.loading && !opps.data ? <Loading rows={5} /> : outcome !== "Open" ? (
        <ClosedList rows={opps.data ?? []} outcome={outcome} forWhom={forWhom} onOpen={setEditing} />
      ) : (
        <div className="board">
          {stages.map((st) => {
            const cards = (opps.data ?? []).filter((o) => o.stage === st);
            return (
              <div key={st} className="col" data-over={over === st}
                onDragOver={(e) => { if (editable) { e.preventDefault(); setOver(st); } }}
                onDragLeave={() => setOver(null)}
                onDrop={(e) => { setOver(null); move(e.dataTransfer.getData("text/plain"), st); }}>
                <h3>{st}<span className="muted">{cards.length}</span></h3>
                {stageMoney(st) && stageMoney(st).open > 0 && <div className="col-money small muted">{money(stageMoney(st).value)} · {stageMoney(st).probability}% → {money(stageMoney(st).weighted)}{stageMoney(st).avg_days != null ? ` · avg ${stageMoney(st).avg_days}d here` : ""}</div>}
                {cards.map((o) => (
                  <div key={o.id} className={clsx("card", o.is_overdue && "overdue", !o.is_overdue && o.is_stale && "stale", (o as any).days_in_stage > 30 && "aged")}
                    draggable={editable} onDragStart={(e) => e.dataTransfer.setData("text/plain", o.id)}
                    onClick={() => setEditing(o)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setEditing(o)}>
                    <b>{o.full_name}{forWhom(o) ? <span className="muted" style={{ fontWeight: 400 }}>, for {forWhom(o)}</span> : null}</b>
                    <span className={o.missing_next_action ? "pill alert" : "muted"}>
                      {o.missing_next_action ? "Set a next action" : `${o.next_action}${o.next_action_on ? ` · ${fmtShort(o.next_action_on)}` : ""}`}
                    </span>
                    <span className="small muted">
                      {AREA_LABEL[o.source_area]}{o.est_value ? ` · ${money(o.est_value)}` : ""}{(o as any).days_in_stage != null ? ` · ${(o as any).days_in_stage}d in stage` : ""}{o.is_stale ? ` · idle ${o.days_idle} days` : ""}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {moving && <NextStepSheet oppId={moving.id} newStage={moving.stage} onClose={() => setMoving(null)} onSaved={() => invalidate("pipeline")} />}
      {editing && <OppSheet opp={editing === "new" ? null : editing} board={board} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Won, Lost and Not now: a plain list (latest first), not stage columns. */
function ClosedList({ rows, outcome, forWhom, onOpen }: { rows: Opp[]; outcome: string; forWhom: (o: any) => string; onOpen: (o: Opp) => void }) {
  if (!rows.length) return <Empty title={`No ${outcome.toLowerCase()} deals`}>{outcome === "Won" ? "Deals you win show up here." : "Deals closed this way show up here."}</Empty>;
  const won = outcome === "Won";
  return (
    <div className="table-wrap"><table className="data">
      <thead><tr><th>Deal with</th><th>{won ? "Won on" : "Closed on"}</th>{won ? <><th className="num">Premium</th><th className="num">Points</th></> : <th>Reason</th>}<th>Came from</th><th className="num">Est. value</th></tr></thead>
      <tbody>{rows.map((o: any) => (
        <tr key={o.id} onClick={() => onOpen(o)} style={{ cursor: "pointer" }}>
          <td><b>{o.full_name}</b>{forWhom(o) ? <span className="muted">, for {forWhom(o)}</span> : null}<div className="small muted">{o.stage}</div></td>
          <td>{o.closed_on ? fmtDate(o.closed_on) : "—"}</td>
          {won ? <><td className="num">{o.life_premium ? money(o.life_premium) : "—"}</td><td className="num">{o.points ?? "—"}</td></>
               : <td>{o.lost_reason ?? (o.next_action ? <span className="muted">{o.next_action}{o.next_action_on ? ` · ${fmtShort(o.next_action_on)}` : ""}</span> : "—")}</td>}
          <td>{AREA_LABEL[o.source_area as Area] ?? "—"}</td>
          <td className="num">{o.est_value ? money(o.est_value) : "—"}</td>
        </tr>))}</tbody>
    </table></div>
  );
}
